/**
 * Global Variables API DTO ↔ 도메인 모델 변환
 */

import type { GlobalFilterKeyValue } from '@/api-client/dashboard/model';
import {
  _RequestPathRule,
  _RequestPlatform,
  type _RequestBody,
  type TagRequest,
  type TreeTagResponseData,
} from '@/api-client/metav8Over/model';
import { _UpdateRequestPlatform } from '@/api-client/metav8Over/model/-updaterequestplatform';
import { _UpdateRequestPathRule } from '@/api-client/metav8Over/model/-updaterequestpathrule';
import type { TargetTagType } from '@/common/utils/types';
import { TAG_TYPE_KEY } from '@/common/define/widget.define';
import { SERVER_TAG_SEPARATOR } from '@/common/define/common.define';
import { convertCamelToCapitalStart } from '@/common/utils/core';
import type { VariableData } from '../components/editVariableWindow/types/editVariableWindow.type';
import type { TreeVariableFormData } from '../components/settingTreeVariableWindow/types/settingTreeVariableWindow.type';
import {
  computeDepths,
  getOrderedResolvedKeys,
  isPatternTagType,
  normalizeOwnSingleDepths,
  resolveLeafKey,
  resolveObjectType,
  unresolveObjectType,
} from '../domain/treeVariable.utils';
import type { _UpdateRequest } from '@/api-client/metav8Over/model/-updaterequest';
import type {
  FilterVariableOperationType,
  TargetTagData,
  UpdatedGlobalVariableData,
} from '../domain/globalVariable.types';
import type { _Response } from '@/api-client/metav8Over/model/-response';
import {
  TreeVariablePathRule,
  TreeVariablePlatform,
  type TreeVariable,
} from '../domain/treeVariable.types';
import {
  DEFAULT_OBJECT_TYPE_VALUES,
  TREE_VARIABLE_TAG_TYPE,
  WORKLOAD_TYPE_OPTIONS,
} from '../domain/treeVariable.constants';

const WORKLOAD_TYPE_VALUES = new Set(WORKLOAD_TYPE_OPTIONS.map(({ value }) => value));

// ============================================================================
// API DTO → Domain Model
// ============================================================================

const OPERATION_TYPE_MAP: Record<string, FilterVariableOperationType> = {
  AND: 'AND',
  OR: 'OR',
};

/**
 * API의 operationType 문자열을 FilterVariableOperationType으로 변환
 */
export const convertOperationTypeFromDto = (operationType: string): FilterVariableOperationType =>
  OPERATION_TYPE_MAP[operationType] ?? 'AND';

/**
 * API의 GlobalFilterKeyValue를 TargetTagData로 변환
 */
export const convertGlobalFilterValueToTargetTagData = (
  target: GlobalFilterKeyValue,
): TargetTagData => ({
  groupTagId: target.tagKey ?? '',
  groupTagName: convertCamelToCapitalStart(target.tagKey?.replace(SERVER_TAG_SEPARATOR, '') ?? ''),
  tagId: target.tagValueId ?? '',
  tagName: target.tagName ?? '',
  tagType: (target.tagType as TargetTagType) ?? TAG_TYPE_KEY.TAG,
  tagAlias: target.alias ?? '',
});

/**
 * API의 _Response를 TreeVariable 도메인 모델로 변환
 */
export const convertResponseToTreeVariable = (response: _Response): TreeVariable => ({
  variableId: response.id!,
  dashboardId: response.dashboardId,
  platform: (response.platform as TreeVariablePlatform) ?? 'HOST',
  alias: response.alias ?? '',
  tagOperationMethod: response.tagOperationMethod as 'AND' | 'OR' | undefined,
  pathRule: response.pathRule as TreeVariablePathRule | undefined,
  pathSubType: (response as _Response & { pathSubType?: string }).pathSubType,
  depth1: response.depth1,
  depth2: response.depth2,
  depth3: response.depth3,
  depth4: response.depth4,
  objectType: response.objectType ?? '',
  targetFilterType: (response.targetFilterType as 'PATTERN' | 'MANUAL') ?? 'MANUAL',
  targetValue:
    response.targetValue?.map(({ tagKey, tagType, tagValueId }) => ({
      tagKey,
      tagType,
      tagValueId,
    })) ?? [],
  createdDate: response.createdDate,
  modifiedDate: response.modifiedDate,
  singlePlatforms: response.singlePlatforms,
});

/**
 * TreeVariable 도메인 모델을 편집 폼 데이터로 변환
 * - flat targetValue[]를 tagKey별 depthSelections Record로 그룹핑
 */
export const convertTreeVariableToFormData = (tree: TreeVariable): TreeVariableFormData => {
  // objectType이 workload 서브타입(deployment 등)이면 objectType='workload', workloadType 설정
  const isWorkloadSubtype = WORKLOAD_TYPE_VALUES.has(tree.objectType);
  const objectType = isWorkloadSubtype
    ? 'workload'
    : unresolveObjectType(tree.platform, tree.objectType);

  // workloadType: pathSubType에서 복원, fallback으로 objectType 또는 depths에서 찾기
  const workloadType = tree.pathSubType ?? (isWorkloadSubtype ? tree.objectType : undefined);

  const depths = computeDepths(objectType, tree.pathRule);

  // flat targetValue[] → 부모는 depthSelections, leaf는 leafSelections으로 분리
  // 응답 tv.tagKey 는 resolved (예: 'host'), tree.objectType 은 raw (예: 'list') 이므로
  // resolveLeafKey 로 양쪽을 resolved 로 통일해 leaf entry 를 식별한다 (domain/SPEC.md 참조).
  const depthSelections: Record<string, { checked: boolean; ids: string[] }> = {};
  const leafSelections: string[] = [];
  const resolvedLeafKey = resolveLeafKey(tree.platform, objectType, workloadType);

  for (const tv of tree.targetValue) {
    if (isPatternTagType(tv.tagType)) continue; // PATTERN은 keyWord 필드로 처리
    if (tv.tagKey === resolvedLeafKey) {
      leafSelections.push(tv.tagValueId);
    } else {
      const entry = depthSelections[tv.tagKey] ?? { checked: true, ids: [] };
      entry.ids.push(tv.tagValueId);
      depthSelections[tv.tagKey] = entry;
    }
  }

  return {
    platform: tree.platform,
    alias: tree.alias,
    tagOperationMethod: tree.tagOperationMethod ?? 'OR',
    objectType,
    workloadType,
    pathRule: tree.pathRule,
    depths,
    targetFilterType: tree.targetFilterType,
    keyWord:
      tree.targetFilterType === 'PATTERN'
        ? (tree.targetValue.find((tv) => isPatternTagType(tv.tagType))?.tagValueId ?? '')
        : '',
    depthSelections,
    leafSelections: leafSelections.length > 0 ? leafSelections : ['*'],
    depthSelectionModes: Object.fromEntries(
      (tree.singlePlatforms ?? []).map((key) => [key, 'SINGLE' as const]),
    ),
  };
};

// ============================================================================
// Domain Model → API DTO
// ============================================================================

/**
 * VariableData를 API 요청용 UpdatedGlobalVariableData로 변환
 */
export const convertToUpdatedGlobalVariableData = (
  variableData: VariableData,
): UpdatedGlobalVariableData => {
  return {
    globalFilterId: +variableData.variableId,
    alias: variableData.aliasName,
    targets: variableData.selectedTagList.map(({ groupTagId, tagId, tagType }) => ({
      tagKey: groupTagId,
      tagValueId: tagId,
      tagType,
    })),
    isSingle: !variableData.isMultiSelectType,
    isTarget: variableData.variableType === 'target',
  };
};

const PLATFORM_MAP: Record<TreeVariablePlatform, _RequestPlatform> = {
  HOST: _RequestPlatform.HOST,
  NETWORK_DEVICE: _RequestPlatform.NETWORK_DEVICE,
  MESSAGE_QUEUE: _RequestPlatform.MESSAGE_QUEUE,
  CLOUD: _RequestPlatform.CLOUD,
  KUBERNETES: _RequestPlatform.KUBERNETES,
  APPLICATION: _RequestPlatform.APPLICATION,
  DATABASE: _RequestPlatform.DATABASE,
  BUSINESS: _RequestPlatform.BUSINESS,
  RUM: _RequestPlatform.RUM,
  WEBSERVER: _RequestPlatform.WEBSERVER,
};

const PATH_RULE_MAP: Record<
  Extract<TreeVariablePathRule, 'NAMESPACE' | 'NODE'>,
  _RequestPathRule
> = {
  NAMESPACE: _RequestPathRule.NAMESPACE,
  NODE: _RequestPathRule.NODE,
};

/**
 * depths의 'workload' 값을 workloadType으로 치환 (필터링 없음)
 * - 로컬 TreeVariable 변환용 (full hierarchy 유지)
 * - API 요청 빌드에는 buildRequestDepths 사용
 */
export const resolveWorkloadDepths = (
  depths: TreeVariableFormData['depths'],
  workloadType?: string,
): TreeVariableFormData['depths'] => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(depths)) {
    if (!value) continue;
    result[key] = value === 'workload' && workloadType ? workloadType : value;
  }
  return result as TreeVariableFormData['depths'];
};

/**
 * formData를 기반으로 API 요청용 depths를 빌드.
 *
 * 동작:
 * - form.depths value 는 raw 폼 키 ('group'/'cluster'/'workload' 등).
 *   - 'workload' 는 workloadType 이 있을 때 실제 값('deployment'/'daemonset' 등) 으로 압축한다
 *     (백엔드 약속: workload depth 는 실제 값으로 전송 + mapping tree 노드 key 도 동일하게 압축됨).
 *   - 압축된 값을 그대로 request body 의 depth1~4 에 출력한다.
 * - depthSelections 조회는 압축된 값을 `resolveObjectType(platform, ...)` 변환한 결과(=resolved 키)로 한다.
 *   depthSelections 의 키 형식이 resolved 이기 때문 (settingTreeVariableWindow 의
 *   useMappingFlow.toggleParentNodeChecked / useAddVariablePanel.resolveNodeKey 약속).
 * - depthSelections 에서 checked=true 인 depth 만 포함. depth1~depth4 순서로 재정렬.
 *
 * 백엔드 API 약속: domain/SPEC.md "Raw vs Resolved 키 — 백엔드 API 약속" 절 참조.
 */
export const buildRequestDepths = (
  form: Pick<TreeVariableFormData, 'depths' | 'workloadType' | 'depthSelections'>,
  platform: TreeVariablePlatform,
): TreeVariableFormData['depths'] => {
  const compactValue = (value: string): string =>
    value === 'workload' && form.workloadType ? form.workloadType : value;

  const checkedValues = Object.values(form.depths)
    .filter((value): value is string => !!value)
    .map(compactValue)
    .filter(
      (compacted) => form.depthSelections[resolveObjectType(platform, compacted)]?.checked === true,
    );

  const result: Record<string, string> = {};
  checkedValues.forEach((value, idx) => {
    result[`depth${idx + 1}`] = value;
  });
  return result as TreeVariableFormData['depths'];
};

const getResolvedLeafKey = (form: TreeVariableFormData): string =>
  resolveLeafKey(form.platform, form.objectType, form.workloadType);

/**
 * 저장할 단일선택 depth 목록 (요청 body singlePlatforms 용).
 * 사용자 지정(own) 단일인 depth 만 저장한다.
 * - 부모: **체크되어 변수에 실제 포함되는** 것만 (미체크 부모는 depth1~4 로 저장 안 됨).
 * - leaf: 항상 변수에 포함되므로 checked 개념 없이 own 단일이면 저장(checked 필터 bypass).
 */
export const buildSingleSelectDepths = (form: TreeVariableFormData): string[] => {
  const leafKey = getResolvedLeafKey(form);
  return normalizeOwnSingleDepths(
    getOrderedResolvedKeys(form.platform, form.objectType, form.pathRule, form.workloadType),
    form.depthSelectionModes,
  ).filter((key) => key === leafKey || form.depthSelections[key]?.checked === true);
};

const getTagsRequestObjectType = (form: TreeVariableFormData): string => {
  if (DEFAULT_OBJECT_TYPE_VALUES.includes(form.objectType)) {
    return form.objectType;
  }

  return form.objectType === 'workload' && form.workloadType
    ? form.workloadType
    : resolveObjectType(form.platform, form.objectType);
};

const buildTagRequestTargetValue = (
  form: TreeVariableFormData,
  options?: { filterWildcard?: boolean; forceWildcardKeys?: Set<string> },
): TagRequest[] => {
  const entries: TagRequest[] = [];
  const manualTagType = TREE_VARIABLE_TAG_TYPE.MANUAL[form.tagOperationMethod];

  const resolveTagValue = (key: string, ids: string[]): string[] => {
    if (options?.forceWildcardKeys?.has(key)) return ['*'];
    const filtered = options?.filterWildcard ? ids.filter((id) => id !== '*') : ids;
    return filtered.length > 0 ? filtered : ['*'];
  };

  // 부모 depth entries
  // 선택된(checked) 카테고리는 항상 entry 를 포함한다. 보낼 값이 비면 ['*'] 로 fallback.
  for (const [tagKey, { checked, ids }] of Object.entries(form.depthSelections)) {
    if (!checked) continue;
    entries.push({ tagKey, tagType: manualTagType, tagValue: resolveTagValue(tagKey, ids) });
  }

  // leaf entry: targetFilterType 에 따라 MANUAL 또는 PATTERN 중 하나만 추가
  const resolvedLeafKey = getResolvedLeafKey(form);
  if (form.targetFilterType === 'MANUAL') {
    entries.push({
      tagKey: resolvedLeafKey,
      tagType: manualTagType,
      tagValue: resolveTagValue(resolvedLeafKey, form.leafSelections),
    });
  } else if (form.targetFilterType === 'PATTERN' && form.keyWord?.trim()) {
    const patternTagType = TREE_VARIABLE_TAG_TYPE.PATTERN[form.tagOperationMethod];
    entries.push({
      tagKey: getResolvedLeafKey(form),
      tagType: patternTagType,
      tagValue: [form.keyWord.trim()],
    });
  }

  return entries;
};

/**
 * TreeVariableFormData를 API 요청용 _RequestBody로 변환
 * - filterWildcard: '*' 항목 제외 여부 (태그 조회 API에서 true, 저장 API에서 false)
 * - forceWildcardKeys: 해당 key 의 ids 를 무시하고 tagValue 를 ['*']로 강제. 양방향 필터링 시
 *   현재 열린 노드 자기 자신은 wildcard 로 보내 응답이 사용자 선택값으로 좁혀지지 않게 한다.
 * - 백엔드 약속: 체크된 부모와 leaf 노드는 항상 entry 로 포함 (값이 없으면 ['*'])
 */
export const convertTreeFormToTagsRequestBody = (
  form: TreeVariableFormData,
  options?: { filterWildcard?: boolean; forceWildcardKeys?: Set<string> },
): _RequestBody => {
  // form.platform 은 TreeVariablePlatform | '' 이지만 본 함수는 platform 이 선택된 후에만 호출된다
  // (변수 저장/태그 조회 시점). PLATFORM_MAP 인덱싱과 buildRequestDepths 양쪽에 이 invariant 가 필요.
  const platform = form.platform as TreeVariablePlatform;
  const singleSelectDepths = buildSingleSelectDepths(form);
  return {
    platform: PLATFORM_MAP[platform],
    alias: form.alias,
    objectType: getTagsRequestObjectType(form),
    targetFilterType: form.targetFilterType,
    tagOperationMethod: form.tagOperationMethod,
    targetValue: buildTagRequestTargetValue(form, options),
    ...(form.pathRule && { pathRule: PATH_RULE_MAP[form.pathRule] }),
    ...(form.workloadType && { pathSubType: form.workloadType }),
    ...buildRequestDepths(form, platform),
    ...(singleSelectDepths.length > 0 && { singlePlatforms: singleSelectDepths }),
  };
};

const UPDATE_PLATFORM_MAP: Record<TreeVariablePlatform, _UpdateRequestPlatform> = {
  HOST: _UpdateRequestPlatform.HOST,
  NETWORK_DEVICE: _UpdateRequestPlatform.NETWORK_DEVICE,
  MESSAGE_QUEUE: _UpdateRequestPlatform.MESSAGE_QUEUE,
  CLOUD: _UpdateRequestPlatform.CLOUD,
  KUBERNETES: _UpdateRequestPlatform.KUBERNETES,
  APPLICATION: _UpdateRequestPlatform.APPLICATION,
  DATABASE: _UpdateRequestPlatform.DATABASE,
  BUSINESS: _UpdateRequestPlatform.BUSINESS,
  RUM: _UpdateRequestPlatform.RUM,
  WEBSERVER: _UpdateRequestPlatform.WEBSERVER,
};

const UPDATE_PATH_RULE_MAP: Record<
  Extract<TreeVariablePathRule, 'NAMESPACE' | 'NODE'>,
  _UpdateRequestPathRule
> = {
  NAMESPACE: _UpdateRequestPathRule.NAMESPACE,
  NODE: _UpdateRequestPathRule.NODE,
};

/**
 * tagData(현 시점 백엔드 응답)에 존재하지 않는 target_id를 form 에서 제거.
 * 모두 제거되어 비면 ['*'] 로 폴백한다 (전체 선택과 동일 동작).
 * tagData 에 해당 키 정보가 없으면 정리를 스킵한다 (응답 누락 시 데이터 손실 방지).
 */
export const cleanDeletedTargetIdsFromForm = (
  form: TreeVariableFormData,
  tagData: TreeTagResponseData,
): TreeVariableFormData => {
  const cleanIds = (ids: string[], key: string): string[] => {
    const tags = tagData[key];
    if (!tags) return ids;
    const validIds = new Set(tags.map((t) => t.id));
    const filtered = ids.filter((id) => id === '*' || validIds.has(id));
    return filtered.length > 0 ? filtered : ['*'];
  };

  const depthSelections = Object.fromEntries(
    Object.entries(form.depthSelections).map(([tagKey, entry]) => [
      tagKey,
      { ...entry, ids: cleanIds(entry.ids, tagKey) },
    ]),
  );

  return {
    ...form,
    depthSelections,
    leafSelections: cleanIds(form.leafSelections, getResolvedLeafKey(form)),
  };
};

/**
 * TreeVariableFormData를 수정 API 요청용 _UpdateRequest로 변환
 */
export const convertTreeFormToUpdateRequestBody = (form: TreeVariableFormData): _UpdateRequest => {
  // form.platform 은 TreeVariablePlatform | '' 이지만 본 함수는 platform 이 선택된 후에만 호출된다.
  const platform = form.platform as TreeVariablePlatform;
  const singleSelectDepths = buildSingleSelectDepths(form);
  return {
    platform: UPDATE_PLATFORM_MAP[platform],
    alias: form.alias,
    objectType: getTagsRequestObjectType(form),
    targetFilterType: form.targetFilterType,
    tagOperationMethod: form.tagOperationMethod,
    targetValue: (() => {
      const manualTagType: string = TREE_VARIABLE_TAG_TYPE.MANUAL[form.tagOperationMethod];

      // 부모 depth entries
      const entries = Object.entries(form.depthSelections)
        .filter(([, { checked }]) => checked)
        .flatMap(([tagKey, { ids }]) =>
          ids.map((tagValueId) => ({ tagKey, tagType: manualTagType, tagValueId })),
        );

      // leaf entry: targetFilterType 에 따라 MANUAL 또는 PATTERN 중 하나만 추가
      const resolvedLeafKey = getResolvedLeafKey(form);
      if (form.targetFilterType === 'MANUAL') {
        for (const tagValueId of form.leafSelections) {
          entries.push({ tagKey: resolvedLeafKey, tagType: manualTagType, tagValueId });
        }
      } else if (form.targetFilterType === 'PATTERN' && form.keyWord?.trim()) {
        entries.push({
          tagKey: getResolvedLeafKey(form),
          tagType: TREE_VARIABLE_TAG_TYPE.PATTERN[form.tagOperationMethod],
          tagValueId: form.keyWord.trim(),
        });
      }

      return entries;
    })(),
    ...(form.pathRule && { pathRule: UPDATE_PATH_RULE_MAP[form.pathRule as 'NAMESPACE' | 'NODE'] }),
    ...(form.workloadType && { pathSubType: form.workloadType }),
    ...buildRequestDepths(form, platform),
    ...(singleSelectDepths.length > 0 && { singlePlatforms: singleSelectDepths }),
  };
};
